import { put, select, takeLatest, call, all } from 'redux-saga/effects';
import _ from 'lodash';
import moment from 'moment';
import uuidv4 from 'uuid/v4';
import { t } from '../../../locales';
import { showErrorModal } from '../../../actions/uiActions';
import {
  FETCH_DASHBOARD_POLICY,
  UPDATE_DASHBOARD_POLICY_LOADING,
  FETCH_DASHBOARD_POLICY_SUCCESS,
} from '../actions/dashboardAction';
import {
  getDashboardPolicyApi,
  getAxaGoalLevel,
} from '../selectors/dashboardSelector';
import { getPolicyListApiUrl } from '../../../selectors/configSelectors';
import { getLanguage } from '../../../selectors/userSelectors';
import {
  AXA_GOAL_DASHBOARD_SCENARIO_1,
  AXA_GOAL_DASHBOARD_SCENARIO_2,
  AXA_GOAL_DASHBOARD_SCENARIO_3,
  AXA_GOAL_DASHBOARD_SCENARIO_4,
  AXA_GOAL_DASHBOARD_SCENARIO_5,
  AXA_GOAL_DASHBOARD_SCENARIO_6,
} from '../constants/constants';
import { myAxaFetchWithToken } from '../../../utils/myAxaFetchWithToken';
import { isApp, stringFormatter } from '../../../utils';
import {
  OWNER_ROLE_NAME,
  INSURED_ROLE_NAME,
  NUMBER_OF_MAX_CARD_SHOWING,
} from '../constants/constants';

function getPolicyTitle(
  hasOwnOnly: boolean,
  hasInsOnly: boolean,
  hasOwnAndIns: boolean,
) {
  const yourAndYourFamilyPolicy = t(
    'AXA_GOAL_DASHBOARD_YOUR_AND_FAMILY_POLICY',
  );
  const youFamilyPolicy = t('AXA_GOAL_DASHBOARD_YOUR_FAMILY');
  const yourPolicy = t('AXA_GOAL_DASHBOARD_YOUR_POLICY');
  const hasAnyInsured = hasInsOnly || hasOwnAndIns;
  if (hasOwnOnly && hasAnyInsured) {
    return yourAndYourFamilyPolicy;
  } else if (hasOwnOnly) {
    return youFamilyPolicy;
  } else if (hasAnyInsured) {
    return yourPolicy;
  } else {
    console.warn(
      'Does not expect to reach this condition. All cases should be covered above',
    );
    return yourPolicy;
  }
}

function getScenario(policy: any) {
  try {
    if (
      !policy ||
      policy.axaGoalPolicy !== 'Y' ||
      policy.axaGoalEnrollFlag !== 'Y'
    ) {
      return false;
    }
    if (shouldShowError(policy)) {
      return AXA_GOAL_DASHBOARD_SCENARIO_6;
    }
    if (shouldGrantAccess(policy)) {
      return AXA_GOAL_DASHBOARD_SCENARIO_1;
    }
    if (shouldRemindAccess(policy)) {
      return AXA_GOAL_DASHBOARD_SCENARIO_2;
    }
    if (shouldRemindEnrollment(policy)) {
      return AXA_GOAL_DASHBOARD_SCENARIO_3;
    }
    if (shouldWarnTooYoung(policy)) {
      return AXA_GOAL_DASHBOARD_SCENARIO_5;
    }
    if (isValidAxaGoalPolicyVisible(policy)) {
      return AXA_GOAL_DASHBOARD_SCENARIO_4;
    }
    // the above conditions should cover all cases, else raise exception
    return AXA_GOAL_DASHBOARD_SCENARIO_6;
  } catch (err) {
    console.error(err);
    return AXA_GOAL_DASHBOARD_SCENARIO_6;
  }
}
function shouldGrantAccess(policy: any) {
  if (isOnlyOwner(policy) && policy.insuredAccessFlag !== 'Y') {
    return true;
  }
  return false;
}
function shouldRemindAccess(policy: any) {
  if (
    isOnlyOwner(policy) &&
    policy.insuredAccessFlag !== 'N' &&
    policy.isInsuredAxaGoalEnrolled === 'N'
  ) {
    return true;
  }
  return false;
}
function shouldRemindEnrollment(policy: any) {
  if (isOwnAndInsured(policy) && policy.isInsuredAxaGoalEnrolled === 'N') {
    return true;
  }
  if (
    isOnlyInsured(policy) &&
    policy.insuredAccessFlag === 'Y' &&
    policy.isInsuredAxaGoalEnrolled === 'N'
  ) {
    return true;
  }
  return false;
}
function shouldWarnTooYoung(policy: any) {
  // This AC has been canceled from the story.
  return false;
}
function shouldShowError(policy: any) {
  if (!policy.partyRoleCd.length) {
    return true;
  }
  if (policy.stepCountMultiAxaIdErrorFlag === 'Y') {
    return true;
  }
  return false;
}
function isValidAxaGoalPolicyVisible(policy: any) {
  if (policy.isOwnerInsuredFlag !== 'Y' && policy.insuredAccessFlag === 'N') {
    // 'Y' or null are still valid
    return false;
  }
  if (
    policy.isOwnerInsuredFlag !== 'Y' &&
    policy.isInsuredAxaGoalEnrolled !== 'Y'
  ) {
    return false;
  }
  return true;
}
function isOnlyOwner(policy: any) {
  if (
    policy.partyRoleCd.length === 1 &&
    policy.partyRoleCd.includes(OWNER_ROLE_NAME)
  ) {
    return true;
  }
  return false;
}
function isOnlyInsured(policy: any) {
  if (
    policy.partyRoleCd.length === 1 &&
    policy.partyRoleCd.includes(INSURED_ROLE_NAME)
  ) {
    return true;
  }
  return false;
}
function isOwnAndInsured(policy: any) {
  if (
    policy.partyRoleCd.includes(OWNER_ROLE_NAME) &&
    policy.partyRoleCd.includes(INSURED_ROLE_NAME)
  ) {
    return true;
  }
  return false;
}

function* fetchDashboardPolicy(payload: any) {
  try {
    yield put({ type: UPDATE_DASHBOARD_POLICY_LOADING, isLoading: true });
    const data = yield getDashboardPolicies();
    if (!data) {
      return;
    }
    const language: any = yield select(getLanguage);
    const dashboardPolicies: any = [];
    const policies: any = data || [];
    for (const item of policies) {
      const {
        policyNumber,
        entityCode,
        insuredName,
        planName = {},
        membershipYearEndDate,
        isLapsedPolicy = '',
      } = item;
      const totalAchieveDaysWithBonus = _.get(
        item,
        'stepCountDetail.totalAchievedDaysWithBonus',
        '0',
      );
      const axaGoalLevel = yield select(
        getAxaGoalLevel,
        totalAchieveDaysWithBonus,
      );
      const scenario = getScenario(item);
      if (!scenario) {
        continue;
      }
      dashboardPolicies.push({
        ...axaGoalLevel,
        isShowRedDot: !(
          scenario === AXA_GOAL_DASHBOARD_SCENARIO_4 ||
          scenario === AXA_GOAL_DASHBOARD_SCENARIO_5
        ),
        planName: planName[language],
        policyNumber,
        entityCode,
        insuredName,
        membershipEndDate:
          membershipYearEndDate && scenario === AXA_GOAL_DASHBOARD_SCENARIO_4
            ? moment(membershipYearEndDate).format('YYYY/MM/DD')
            : '',
        scenario,
        policyDetails: item,
        isLapsedPolicy: isLapsedPolicy === 'Y' ? true : false,
      });
    }
    sortDashbaordPoliciesOrder(dashboardPolicies);
    const {
      policyTitle,
      isShowViewAllCard,
      policyLength,
      isShowTrackingDevice,
    } = getVariables(dashboardPolicies);

    yield put({
      type: FETCH_DASHBOARD_POLICY_SUCCESS,
      payload: {
        dashboardPolicies,
        policyTitle,
        isShowViewAllCard,
        policyLength,
        isShowTrackingDevice,
      },
    });
    yield put({ type: UPDATE_DASHBOARD_POLICY_LOADING, isLoading: false });
  } catch (e) {
    yield put({ type: UPDATE_DASHBOARD_POLICY_LOADING, isLoading: false });
    yield put(showErrorModal());
  }
}

function* getDashboardPolicies() {
  const policyUrl = yield select(getDashboardPolicyApi);
  if (!policyUrl) {
    return false;
  }
  const res = yield call(myAxaFetchWithToken, policyUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-axahk-msgid': uuidv4(),
    },
  });
  const data = yield res.json();
  // if (!data) {
  //   const resMock = {
  //     status: 200,
  //     json: () => require("../mock/dashboard.json"),
  //   };
  //   return yield resMock.json();
  // }
  return data;
}

function sortDashbaordPoliciesOrder(dashboardPolicies: any) {
  dashboardPolicies.sort((policyA: any, policyB: any) => {
    // sort by policy owner, owner=ins first, ins second
    if (
      policyA.policyDetails.isOwnerInsuredFlag &&
      !policyB.policyDetails.isOwnerInsuredFlag
    ) {
      return 1;
    }
    if (
      !policyA.policyDetails.isOwnerInsuredFlag &&
      policyB.policyDetails.isOwnerInsuredFlag
    ) {
      return -1;
    }
    // sort by membershipYearEndDate
    if (!policyA.policyDetails.membershipYearEndDate) {
      return -1;
    }
    if (!policyB.policyDetails.membershipYearEndDate) {
      return 1;
    }
    if (
      policyA.policyDetails.membershipYearEndDate >
      policyB.policyDetails.membershipYearEndDate
    ) {
      return 1;
    }
    if (
      policyA.policyDetails.membershipYearEndDate <
      policyB.policyDetails.membershipYearEndDate
    ) {
      return -1;
    }
    return 0;
  });
}

function getVariables(dashboardPolicies: any) {
  const isShowViewAllCard =
    dashboardPolicies.length > NUMBER_OF_MAX_CARD_SHOWING;
  const policyLength = dashboardPolicies.length;
  const hasAxaGoalInsuredPolicy = !!dashboardPolicies.find(
    (item: any) =>
      item.policyDetails.axaGoalEnrollFlag === 'Y' &&
      item.policyDetails.partyRoleCd.includes(INSURED_ROLE_NAME),
  );
  const isShowTrackingDevice = isApp() && hasAxaGoalInsuredPolicy;
  const hasOwnOnly = !!dashboardPolicies.find(
    (item: any) =>
      item.policyDetails.partyRoleCd.length === 1 &&
      item.policyDetails.partyRoleCd.includes('OWN'),
  );
  const hasInsOnly = !!dashboardPolicies.find(
    (item: any) =>
      item.policyDetails.partyRoleCd.length === 1 &&
      item.policyDetails.partyRoleCd.includes('INS'),
  );
  const hasOwnAndIns = !!dashboardPolicies.find(
    (item: any) =>
      item.policyDetails.partyRoleCd.length === 2 &&
      item.policyDetails.partyRoleCd.includes('OWN') &&
      item.policyDetails.partyRoleCd.includes('INS'),
  );
  const policyTitle = getPolicyTitle(hasOwnOnly, hasInsOnly, hasOwnAndIns);
  return { policyTitle, isShowViewAllCard, policyLength, isShowTrackingDevice };
}

function* dashboardSaga(): any {
  yield takeLatest(FETCH_DASHBOARD_POLICY, fetchDashboardPolicy);
}

export default dashboardSaga;
